import {
  LayoutOrderContainer,
  LayoutStickySection,
  LayoutTabSection,
} from './orders/_layouts/LayoutOrderContainer';
import { RevieswsProvider } from './orders/_components/reviews/ReviewsProvider';
import TabSection from './orders/_components/reviews/TabSection';
import TableSection from './orders/_components/reviews/TableSection';
import MarketTabSectionReview from './orders/_components/common/MarketTabSectionReview';
import { useReviews } from './orders/_components/reviews/ReviewsProvider';
import { useAppSelector } from '@/redux/store';
import { useMemo } from 'react';
import { CPLAT_API_ROUTES } from '@/api';
import { fetcher } from '@/api';
import { useQueries, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { MarketStoreListItem } from './orders/_consts/constants';
import { useOrdersByMarket } from './orders/_hooks/useMarketReviews';
import { getMarketKey } from './orders/_utils/getMarketKey';
import { type ReviewItem } from '@/types/api';
import {
  filterReviewsByDate,
  sortReviews,
  getTabListWithCounts,
  filterReviewsByTab,
} from './orders/_utils/reviews';
import {
  LayoutReviewControllerSection,
  LayoutReviewControllerContent,
  LayoutReviewScrollArea,
} from './orders/_layouts/LayoutsReviews';
import { ContentsTab } from './orders/_components/reviews/ContentsTab';
import DateRangeSelect from './orders/_components/common/DateRangeSelect';
import ReviewsFilterSelect from './orders/_components/reviews/ReviewsFilterSelect';
import EditTemplate from './orders/_components/reviews/EditTemplate';
import AlertDialog from '@/components/dashboard/alert-dialog';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import {
  membershipReviewPreviewDetailQuery,
  membershipReviewPreviewMarketCounts,
} from './orders/_components/membership-review-inquiry-mock';

type ReviewDetailQueryData = {
  data: Array<{
    ReportDetailList: ReviewItem[];
  }>;
};

type ReviewDetailQueryOptions = UseQueryOptions<ReviewDetailQueryData>;

const ReviewsPage = () => {
  const isPreviewMode = useMembershipPreviewMode();

  return (
    <RevieswsProvider>
      {!isPreviewMode && <AlertDialog />}
      <ReviewsContent />
    </RevieswsProvider>
  );
};

export default ReviewsPage;

const ReviewsContent = () => {
  const {
    currentTab,
    marketList,
    selectedMarket,
    setSelectedMarket,
    startDate,
    endDate,
    handleStartDate,
    handleEndDate,
    filterSelect,
  } = useReviews();
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();

  const allMarketsParams = useMemo(
    () =>
      marketList.map((market) => ({
        cplatToken: user.CplatToken,
        ...(market?.MarketId !== undefined ? { MarketId: market.MarketId } : {}),
        ...(market?.StoreName !== undefined ? { StoreName: market.StoreName } : {}),
        OrderByMethod: 0,
        Skip: 0,
        Take: 99999,
      })),
    [user.CplatToken, marketList],
  );

  const allMarketsQueries = useMemo<ReviewDetailQueryOptions[]>(
    () =>
      marketList.map((market, idx) => {
        if (isPreviewMode) {
          return membershipReviewPreviewDetailQuery(market);
        }

        return {
          queryKey: [CPLAT_API_ROUTES.getReviewDetail, allMarketsParams[idx]],
          queryFn: () =>
            fetcher<ReviewDetailQueryData>({
              queryKey: [CPLAT_API_ROUTES.getReviewDetail, allMarketsParams[idx]],
              method: 'POST',
            }),
          enabled: !!user.CplatToken,
        };
      }),
    [isPreviewMode, marketList, allMarketsParams, user.CplatToken],
  );

  const allMarketsResults = useQueries({
    queries: allMarketsQueries,
  }) as UseQueryResult<ReviewDetailQueryData>[];

  const ordersByMarket = useOrdersByMarket(marketList, allMarketsResults);

  // 마켓별 주문 건수 (status만)
  const marketOrderCounts = useMemo(() => {
    if (isPreviewMode) return membershipReviewPreviewMarketCounts;

    return marketList.map((market: MarketStoreListItem) => {
      const found = ordersByMarket.find(
        (m: { key: string; orders: ReviewItem[] }) => m.key === getMarketKey(market),
      );
      return { key: getMarketKey(market), orderCount: found?.orders.length ?? 0 };
    });
  }, [isPreviewMode, marketList, ordersByMarket]);

  const tableOrderList = useMemo(() => {
    const selectedKey = selectedMarket ? getMarketKey(selectedMarket) : '';
    const baseOrders = (ordersByMarket.find(
      (m: { key: string; orders: ReviewItem[] }) => m.key === selectedKey,
    )?.orders ?? []) as ReviewItem[];
    const dateFiltered = filterReviewsByDate(baseOrders, startDate, endDate);
    const sorted = sortReviews(dateFiltered, filterSelect.value);

    // tab에 따라 필터링
    return filterReviewsByTab(sorted, currentTab);
  }, [ordersByMarket, selectedMarket, startDate, endDate, filterSelect, currentTab]);

  // 탭별 주문 건수
  const tabListWithCounts = useMemo(() => {
    const selectedKey = selectedMarket ? getMarketKey(selectedMarket) : '';
    const orders = (ordersByMarket.find(
      (m: { key: string; orders: ReviewItem[] }) => m.key === selectedKey,
    )?.orders ?? []) as ReviewItem[];
    const dateFiltered = filterReviewsByDate(orders, startDate, endDate);
    return getTabListWithCounts(dateFiltered);
  }, [ordersByMarket, selectedMarket, startDate, endDate]);

  const isLoading = isPreviewMode ? false : allMarketsResults.some((result) => result.isLoading);
  return (
    <LayoutOrderContainer className="flex-1">
      <LayoutTabSection>
        <TabSection />
      </LayoutTabSection>
      <LayoutStickySection className="max-w-full pb-[0px] pr-[60px]">
        {marketList.length > 1 && (
          <MarketTabSectionReview
            marketList={marketList}
            selectedMarket={selectedMarket}
            setSelectedMarket={setSelectedMarket}
            marketOrderCounts={marketOrderCounts}
          />
        )}

        <LayoutReviewControllerSection>
          <LayoutReviewControllerContent>
            <LayoutReviewScrollArea>
              <DateRangeSelect
                startDate={startDate}
                handleStartDate={handleStartDate}
                endDate={endDate}
                handleEndDate={handleEndDate}
              />
              <ReviewsFilterSelect />
            </LayoutReviewScrollArea>
            <EditTemplate />
          </LayoutReviewControllerContent>
          <ContentsTab tabListWithCounts={tabListWithCounts} />
        </LayoutReviewControllerSection>
      </LayoutStickySection>
      {/* <LayoutReviewBodyContent> */}
      <div className="flex w-full flex-1 flex-col  bg-white  pb-[36px] pt-4">
        <div
          className={`flex  w-full max-w-full flex-1 flex-col rounded-[20px] 
        `}
        >
          <TableSection
            allMarketsQueries={allMarketsQueries}
            isLoading={isLoading}
            tableOrderList={tableOrderList}
          />
        </div>
      </div>
      {/* </LayoutReviewBodyContent> */}
    </LayoutOrderContainer>
  );
};
